import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent,
  type ReactNode,
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBracketsCurly,
  faCircleExclamation,
  faChevronDown,
  faChevronRight,
  faCircle,
  faCircleCheck,
  faCircleXmark,
  faCopy,
  faDownload,
  faGripLines,
  faLayerGroup,
  faRotateRight,
} from '@fortawesome/pro-regular-svg-icons';
import {
  Badge,
  Button,
  ProgressRoot,
  ProgressSection,
  ProgressLabel,
  Text,
  Stack,
  Group,
  Loader,
  ThemeIcon,
  Collapse,
  Tooltip,
  ActionIcon,
  CopyButton,
  Divider,
} from '@cowbell/ui/core';
import {
  getAsyncTask,
  listAsyncTasks,
  cancelAsyncTask,
  retryFailedAsyncTask,
  type AsyncTaskDeliveryArtifact,
  type AsyncTaskDetail,
  type AsyncTaskStagePayload,
  type AsyncTaskResultView,
  type AsyncTaskStep,
  type AsyncTaskSummary,
  type AsyncTaskView,
} from '../../api/async-tasks.api';
import { FileArtifactCard, FormDraftHandoff, formatFileSize } from '@cowbell/ui';
import { useStageExtractedAnswers } from '../../../../console/risk-eng/forms/draft/moo/useStageExtractedAnswers';
import useNotifications from '../../../../_lib/notifications/useNotifications';
import { downloadArtifact } from '../../v2/artifact-download';
import {
  downloadDeliveryResult,
  isTableFormatResult,
} from '../../utils/download-delivery-result';
import { MOO_V2_OPEN_SOURCE_PANEL_EVENT } from '../../v2/source-panel-events';
import { AsyncTaskMarkdown } from './AsyncTaskMarkdown';
import { AsyncTaskUnitRow } from './AsyncTaskUnitRow';
import styles from './AsyncTaskProgress.module.css';

interface AsyncTaskProgressProps {
  readonly runId: string;
  readonly name: string;
  // Set on cards rendered inside a SiblingRow: no sibling section (prevents
  // infinite recursion), no own cancel button (the row already has one), and
  // a light grey background to read as part of the parent card.
  readonly nested?: boolean;
  // Preferred conversation id. The V2 chat keeps the active thread in
  // @cowbell/moo shell state and never updates AskMooProvider, so V2
  // renderers must pass the id explicitly. Falls back to the ambient
  // AskMooProvider conversation when omitted (V1 path).
  readonly conversationId?: string;
  // Only the newest card may drive the chat forward — an old card in history
  // must not fire a follow-up prompt (parity with EmailDraftCard).
  readonly isLastMessage?: boolean;
  readonly onSendMessage?: (message: string, skill?: string) => void;
}

type Status = AsyncTaskDetail['status'];

const STATUS_META: Record<
  Status,
  { readonly color: string; readonly label: string }
> = {
  preparing: { color: 'blue', label: 'Preparing' },
  decomposing: { color: 'blue', label: 'Planning' },
  running: { color: 'blue', label: 'Running' },
  completed: { color: 'teal', label: 'Completed' },
  failed: { color: 'red', label: 'Failed' },
  cancelled: { color: 'gray', label: 'Cancelled' },
};

const TERMINAL = new Set<Status>(['completed', 'failed', 'cancelled']);
const POLL_INTERVAL_MS = 30000;
const STEP_POLL_INTERVAL_MS = 2000;
const ERROR_RETRY_MS = 5000;

const ACCENT_CLASS: Record<Status, string> = {
  preparing: 'accentBlue',
  decomposing: 'accentBlue',
  running: 'accentBlue',
  completed: 'accentTeal',
  failed: 'accentRed',
  cancelled: 'accentGray',
};

function useCancelRun(
  conversationId: string | null | undefined,
  runId: string,
  onCancelled: () => void
) {
  const [cancelling, setCancelling] = useState(false);

  const cancel = async () => {
    if (!conversationId || cancelling) return;
    setCancelling(true);
    try {
      await cancelAsyncTask(conversationId, runId);
      onCancelled();
    } catch {
      // Next poll reconciles with the server's actual state.
    } finally {
      setCancelling(false);
    }
  };

  return { cancel, cancelling };
}

interface RetryRun {
  readonly run_id: string;
  readonly name: string;
}

function useRetryFailed(
  conversationId: string | null | undefined,
  runId: string,
  onStarted: (run: RetryRun) => void
) {
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  // Synchronous double-click guard: the `retrying` state only updates on the
  // next render, so a second click in the same frame would fire a second
  // POST — and the extra retry run would be orphaned (retries have no chat
  // message; only the run attached via onStarted gets a card).
  const inFlightRef = useRef(false);
  // Epoch guard: a response that resolves after a (conversation, run)
  // switch must not attach the previous run's retry child — or its error —
  // to the card's new identity. A monotonic counter (bumped on every
  // identity change) rather than the identity value itself: switching away
  // and back would make a string compare match again (ABA), letting a stale
  // response attach an old retry and unlock inFlightRef under a newer
  // request.
  const epochRef = useRef(0);

  useEffect(() => {
    epochRef.current += 1;
    inFlightRef.current = false;
    setRetrying(false);
    setRetryError(null);
  }, [conversationId, runId]);

  const retry = async () => {
    if (!conversationId || inFlightRef.current) return;
    inFlightRef.current = true;
    const epoch = epochRef.current;
    setRetrying(true);
    setRetryError(null);
    try {
      const res = await retryFailedAsyncTask(conversationId, runId);
      if (epochRef.current !== epoch) return;
      const data = (res as any)?.data ?? res;
      onStarted({ run_id: data.run_id, name: data.name });
    } catch (e: any) {
      if (epochRef.current !== epoch) return;
      // The endpoint 400s with {error} when there is nothing to retry or the
      // source run's state expired from Redis — surface that reason.
      const serverError = e?.response?.data?.error;
      setRetryError(serverError ?? e?.message ?? 'Failed to start retry');
    } finally {
      if (epochRef.current === epoch) {
        inFlightRef.current = false;
        setRetrying(false);
      }
    }
  };

  return { retry, retrying, retryError };
}

function RetryButton({
  retrying,
  onRetry,
}: Readonly<{ retrying: boolean; onRetry: () => void }>) {
  return (
    <Button
      size="compact-xs"
      variant="subtle"
      color="blue.5"
      loading={retrying}
      leftSection={
        <FontAwesomeIcon icon={faRotateRight} style={{ fontSize: 10 }} />
      }
      onClick={onRetry}
    >
      Retry failed
    </Button>
  );
}

function CancelButton({
  cancelling,
  onCancel,
}: Readonly<{ cancelling: boolean; onCancel: () => void }>) {
  return (
    <Button
      size="compact-xs"
      variant="subtle"
      color="red.4"
      loading={cancelling}
      onClick={onCancel}
    >
      Cancel
    </Button>
  );
}

function StatusBadge({
  status,
  finalizing = false,
}: Readonly<{ status: Status; finalizing?: boolean }>) {
  // "Completed" would be a lie while the combine/delivery phase is still
  // running server-side — the run's units are done but the combined result
  // hasn't been written and deliveries haven't gone out yet.
  if (finalizing) {
    return (
      <Badge size="sm" color="blue" variant="light" radius="sm">
        Finalizing
      </Badge>
    );
  }
  const meta = STATUS_META[status];
  return (
    <Badge size="sm" color={meta.color} variant="light" radius="sm">
      {meta.label}
    </Badge>
  );
}

function PreparingLine({ status }: Readonly<{ status: Status }>) {
  return (
    <Group gap="xs">
      <Loader size="xs" />
      <Text size="xs" c="dimmed">
        {status === 'preparing' ? 'Gathering data…' : 'Planning units of work…'}
      </Text>
    </Group>
  );
}

const STEP_ICON: Record<
  AsyncTaskStep['status'],
  { readonly icon: typeof faCircle; readonly color: string }
> = {
  not_started: { icon: faCircle, color: 'gray' },
  in_progress: { icon: faCircle, color: 'blue' },
  completed: { icon: faCircleCheck, color: 'teal' },
  failed: { icon: faCircleXmark, color: 'red' },
};

/** Extraction runs before their step list has been published — no counter,
 *  just a spinner so the card doesn't fall back to the units label. */
/** Placeholder for a checklist run whose steps the server hasn't published
 *  yet. Neutral copy: any `view: 'checklist'` kind lands here, not just
 *  extraction. */
function StartingLine() {
  return (
    <Group gap="xs">
      <Loader size="xs" />
      <Text size="xs" c="dimmed">
        Getting started…
      </Text>
    </Group>
  );
}

/** Named checklist for extraction runs (replaces the processed/total counter).
 *  Collapsible: defaults open while the run is live and collapsed once
 *  terminal, since a finished checklist is mostly noise. */
function StepsChecklist({
  steps,
  defaultOpen,
}: Readonly<{ steps: AsyncTaskStep[]; defaultOpen: boolean }>) {
  const [open, setOpen] = useState(defaultOpen);
  const completed = steps.filter((s) => s.status === 'completed').length;
  const active = steps.find((s) => s.status === 'in_progress');
  const summary = active
    ? active.label
    : `${completed} of ${steps.length} steps`;

  return (
    <>
      <button
        type="button"
        className={styles.unitsToggle}
        onClick={() => setOpen((v) => !v)}
      >
        <FontAwesomeIcon
          icon={open ? faChevronDown : faChevronRight}
          style={{ fontSize: 10 }}
        />
        <span>{open ? 'Hide steps' : summary}</span>
      </button>
      <Collapse in={open}>
        <Stack gap={6} pt={4}>
          {steps.map((step, index) => {
            // Fall back to the neutral idle glyph for any status not in the
            // map, so an unexpected value can never crash the render.
            const meta = STEP_ICON[step.status] ?? STEP_ICON.not_started;
            return (
              <Group key={`${index}-${step.label}`} gap="xs" wrap="nowrap">
                {step.status === 'in_progress' ? (
                  <Loader size="xs" />
                ) : (
                  <ThemeIcon
                    size="xs"
                    radius="xl"
                    color={meta.color}
                    // Transparent, not light: the status glyphs are already
                    // circles, so a tinted background disc reads as a halo /
                    // drop shadow behind each checkmark.
                    variant="transparent"
                  >
                    <FontAwesomeIcon icon={meta.icon} style={{ fontSize: 9 }} />
                  </ThemeIcon>
                )}
                <Text
                  size="xs"
                  c={step.status === 'not_started' ? 'dimmed' : undefined}
                >
                  {step.label}
                </Text>
              </Group>
            );
          })}
        </Stack>
      </Collapse>
    </>
  );
}

/** Visible after all units finish but before the server writes the combined
 *  result and sends deliveries (the whole-view LLM pass runs post-finalize). */
function CombiningLine() {
  return (
    <Group gap="xs">
      <Loader size="xs" />
      <Text size="xs" c="dimmed">
        Combining results and preparing delivery…
      </Text>
    </Group>
  );
}

/** Visible while a bulk harvest was truncated to the unit-cost ceiling. */
function CapNotice({
  cappedFrom,
  total,
}: Readonly<{
  cappedFrom: number | null | undefined;
  total: number;
}>) {
  if (cappedFrom == null || cappedFrom <= total || total <= 0) {
    return null;
  }
  return (
    <Text size="xs" c="dimmed">
      Limited to the first {total} of {cappedFrom} matching entities (system
      limit).
    </Text>
  );
}

function InlineError({ message }: Readonly<{ message: string }>) {
  return (
    <Group gap={6} wrap="nowrap">
      <ThemeIcon size="xs" radius="xl" color="red" variant="light">
        <FontAwesomeIcon icon={faCircleExclamation} style={{ fontSize: 9 }} />
      </ThemeIcon>
      <Text size="xs" c="red">
        {message}
      </Text>
    </Group>
  );
}

function SectionLabel({ children }: Readonly<{ children: string }>) {
  return (
    <Text
      size="xs"
      fw={600}
      c="dimmed"
      tt="uppercase"
      style={{ letterSpacing: 0.4 }}
    >
      {children}
    </Text>
  );
}

function ShardBadge({
  label,
  tooltip,
}: Readonly<{ label: string; tooltip: string }>) {
  return (
    <Tooltip label={tooltip}>
      <Badge
        size="sm"
        color="grape"
        variant="light"
        radius="sm"
        leftSection={
          <FontAwesomeIcon icon={faLayerGroup} style={{ fontSize: 9 }} />
        }
      >
        {label}
      </Badge>
    </Tooltip>
  );
}

function buildShardBadge(
  isShardedGroup: boolean,
  shardCount: number | null,
  shardIdx: number | null
): ReactNode {
  if (shardCount == null || shardCount <= 1) return null;
  if (isShardedGroup) {
    return (
      <ShardBadge
        label={`${shardCount} parts`}
        tooltip={`Split into ${shardCount} parts because the task covered more than 500 items`}
      />
    );
  }
  return (
    <ShardBadge
      label={`${shardIdx}/${shardCount}`}
      tooltip={`Part ${shardIdx} of ${shardCount} — split because the task covered more than 500 items`}
    />
  );
}

function TaskProgressBar({
  pct,
  color,
  size,
  animated = false,
  showLabel = false,
  className,
}: Readonly<{
  pct: number;
  color: string;
  size: 'sm' | 'lg';
  animated?: boolean;
  showLabel?: boolean;
  className?: string;
}>) {
  return (
    <ProgressRoot size={size} radius="sm" className={className}>
      <ProgressSection
        value={pct}
        color={color}
        className={animated ? styles.progressAnimated : undefined}
      >
        {showLabel && pct >= 12 ? <ProgressLabel>{pct}%</ProgressLabel> : null}
      </ProgressSection>
    </ProgressRoot>
  );
}

function PlaceholderCard({
  nested,
  title,
  badge,
  dimmedTitle = false,
  children,
}: Readonly<{
  nested: boolean;
  title: string;
  badge: string;
  dimmedTitle?: boolean;
  children: ReactNode;
}>) {
  return (
    <div
      className={`${styles.card} ${styles.accentGray} ${
        nested ? styles.cardNested : ''
      }`}
    >
      <Group justify="space-between" mb={4}>
        <Text size="sm" fw={600} c={dimmedTitle ? 'dimmed' : undefined}>
          {title}
        </Text>
        <Badge size="sm" color="gray" variant="light" radius="sm">
          {badge}
        </Badge>
      </Group>
      {children}
    </div>
  );
}

function RetryChildCard({
  retryRun,
  conversationId,
}: Readonly<{
  retryRun: RetryRun;
  conversationId: string | null | undefined;
}>) {
  return (
    <>
      <Divider my={2} />
      <SectionLabel>Retry</SectionLabel>
      {/* Full (non-nested) card: the retry run must keep its own Cancel while
          running and its own Retry once terminal — retries only exist on this
          card, so suppressing those controls would strand the run. */}
      <AsyncTaskProgress
        runId={retryRun.run_id}
        name={retryRun.name}
        conversationId={conversationId ?? undefined}
      />
    </>
  );
}

// Downloadable results files from artifact-based delivery: one button per
// file, using the same presign-then-navigate flow as chat artifact cards.
function DeliveryArtifactsRow({
  artifacts,
}: Readonly<{ artifacts: readonly AsyncTaskDeliveryArtifact[] }>) {
  const notifications = useNotifications();

  const handleDownload = (artifact: AsyncTaskDeliveryArtifact) => {
    void downloadArtifact(artifact.presign_path, artifact.filename).catch(
      () => {
        notifications.error(
          'Download failed',
          `Could not download ${artifact.filename}.`
        );
      }
    );
  };

  return (
    <Group gap={8}>
      {artifacts.map((artifact) => (
        <FileArtifactCard
          key={artifact.artifact_id}
          filename={artifact.filename}
          downloadUrl={artifact.presign_path}
          sizeLabel={formatFileSize(artifact.size_bytes) || undefined}
          onDownload={() => handleDownload(artifact)}
        />
      ))}
    </Group>
  );
}

/** Extraction runs surface their result as JSON behind a button (mirrors the
 *  Concierge inbox "View Extracted Data" affordance) instead of an inline
 *  combined-result panel. delivery_result is always a JSON string here; parse
 *  defensively so a malformed payload just opens an empty viewer. Opens the
 *  shared right-docked JsonViewerPanel via the Ask Moo source-panel event
 *  rather than a modal — same viewer, drawer presentation. Rendered as the
 *  neutral secondary pill in the draft-card idiom (EmailDraftCard's "Copy"). */
function ExtractionDataButton({ result }: Readonly<{ result: string }>) {
  const data = useMemo(() => {
    try {
      return JSON.parse(result);
    } catch {
      return null;
    }
  }, [result]);

  const handleOpen = () => {
    globalThis.dispatchEvent(
      new CustomEvent(MOO_V2_OPEN_SOURCE_PANEL_EVENT, {
        detail: { type: 'json', data },
      })
    );
  };

  return (
    <button type="button" className={styles.pillButton} onClick={handleOpen}>
      <FontAwesomeIcon icon={faBracketsCurly} style={{ fontSize: 12 }} />
      View Extracted Data
    </button>
  );
}

/** Primary affirmative CTA on a completed extraction card: resumes the Moo
 *  workflow by sending a follow-up prompt. Styled as the accent pill in the
 *  draft-card idiom — the same bordered button EmailDraftCard uses for "Send"
 *  and NoteDraftCard for "Save", tinted with the card's teal completed accent.
 *  Disabled on stale cards — only the newest turn may drive the chat forward
 *  (parity with EmailDraftCard / NoteDraftCard). */
function ContinueButton({
  isLastMessage,
  onContinue,
}: Readonly<{ isLastMessage: boolean; onContinue: () => void }>) {
  // One-shot: isLastMessage stays true until the next assistant turn lands, so
  // a second click would fire another Continue into the same thread. Latch on
  // the first send and disable — the follow-up turn re-renders a fresh card.
  const [sent, setSent] = useState(false);
  const disabled = !isLastMessage || sent;
  const handleClick = () => {
    if (disabled) return;
    setSent(true);
    onContinue();
  };
  return (
    <button
      type="button"
      className={disabled ? styles.pillPrimaryDisabled : styles.pillPrimary}
      disabled={disabled}
      onClick={handleClick}
    >
      Continue
    </button>
  );
}

function ErrorAndDelivery({
  error,
  deliveryResult,
  deliveryArtifacts,
  isTerminal,
  finalizing,
  taskName,
  // A JSON-result run shows its payload in the "View Extracted Data" viewer,
  // making the combine/delivery block redundant. Prose-result runs keep it.
  hasJsonResult,
}: Readonly<{
  error: string | null | undefined;
  deliveryResult: string | null | undefined;
  deliveryArtifacts: readonly AsyncTaskDeliveryArtifact[] | null | undefined;
  isTerminal: boolean;
  finalizing: boolean;
  taskName: string;
  hasJsonResult: boolean;
}>) {
  if (hasJsonResult) {
    return error ? <InlineError message={error} /> : null;
  }
  const showArtifacts =
    isTerminal && deliveryArtifacts && deliveryArtifacts.length > 0;
  // A downloadable artifact IS the result — the accompanying delivery_result
  // is just a one-line "results are attached" placeholder, so the combined
  // result panel would only duplicate the download card below it.
  const showDeliveryResult = isTerminal && deliveryResult && !showArtifacts;
  return (
    <>
      {error ? <InlineError message={error} /> : null}
      {showDeliveryResult ? (
        <DeliveryResultPanel content={deliveryResult} taskName={taskName} />
      ) : null}
      {showArtifacts ? (
        <DeliveryArtifactsRow artifacts={deliveryArtifacts} />
      ) : null}
      {finalizing ? <CombiningLine /> : null}
    </>
  );
}

function RetryFooter({
  retryError,
  showRetryChild,
  retryRun,
  conversationId,
}: Readonly<{
  retryError: string | null;
  showRetryChild: boolean;
  retryRun: RetryRun | null;
  conversationId: string | null | undefined;
}>) {
  return (
    <>
      {retryError ? <InlineError message={retryError} /> : null}
      {showRetryChild && retryRun ? (
        <RetryChildCard retryRun={retryRun} conversationId={conversationId} />
      ) : null}
    </>
  );
}

const DEFAULT_DELIVERY_HEIGHT_PX = 280;
const MIN_DELIVERY_HEIGHT_PX = 120;
const MAX_DELIVERY_HEIGHT_RATIO = 0.8;

function DeliveryResultPanel({
  content,
  taskName,
}: Readonly<{ content: string; taskName: string }>) {
  const notifications = useNotifications();
  const [heightPx, setHeightPx] = useState(DEFAULT_DELIVERY_HEIGHT_PX);
  const dragStartRef = useRef<{
    readonly pointerY: number;
    readonly heightPx: number;
  } | null>(null);
  const downloadLabel = isTableFormatResult(content)
    ? 'Download CSV'
    : 'Download PDF';

  const handleDownload = () => {
    void downloadDeliveryResult(content, taskName).catch(() => {
      notifications.error('Download failed', 'Could not export this result.');
    });
  };

  const handleResizePointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    dragStartRef.current = { pointerY: event.clientY, heightPx };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleResizePointerMove = (event: PointerEvent<HTMLButtonElement>) => {
    const dragStart = dragStartRef.current;
    if (!dragStart) return;
    const maxHeightPx = Math.round(
      window.innerHeight * MAX_DELIVERY_HEIGHT_RATIO
    );
    const nextHeightPx = Math.min(
      maxHeightPx,
      Math.max(
        MIN_DELIVERY_HEIGHT_PX,
        dragStart.heightPx + (event.clientY - dragStart.pointerY)
      )
    );
    setHeightPx(nextHeightPx);
  };

  const handleResizePointerUp = (event: PointerEvent<HTMLButtonElement>) => {
    dragStartRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  return (
    <div className={styles.deliveryBlock}>
      <Group justify="space-between" mb={4}>
        <SectionLabel>Combined result</SectionLabel>
        <Group gap={2} wrap="nowrap">
          <Tooltip label={downloadLabel} position="top" withArrow>
            <ActionIcon
              size="sm"
              variant="subtle"
              color="gray"
              aria-label={downloadLabel}
              onClick={handleDownload}
            >
              <FontAwesomeIcon icon={faDownload} style={{ fontSize: 11 }} />
            </ActionIcon>
          </Tooltip>
          <CopyButton value={content}>
            {({ copied, copy }) => (
              <ActionIcon
                size="sm"
                variant="subtle"
                color={copied ? 'teal' : 'gray'}
                aria-label="Copy combined result"
                onClick={copy}
              >
                <FontAwesomeIcon icon={faCopy} style={{ fontSize: 11 }} />
              </ActionIcon>
            )}
          </CopyButton>
        </Group>
      </Group>
      <div className={styles.deliveryScroll} style={{ height: heightPx }}>
        <AsyncTaskMarkdown content={content} />
      </div>
      <Tooltip label="Drag to resize" position="bottom" withArrow>
        <button
          type="button"
          className={styles.deliveryResizeHandle}
          aria-label="Drag to resize combined result"
          onPointerDown={handleResizePointerDown}
          onPointerMove={handleResizePointerMove}
          onPointerUp={handleResizePointerUp}
          onPointerCancel={handleResizePointerUp}
        >
          <FontAwesomeIcon
            icon={faGripLines}
            className={styles.deliveryResizeIcon}
          />
        </button>
      </Tooltip>
    </div>
  );
}

/** Progress treatment, falling back to the pre-`view` contract: a backend that
 *  omits these fields only ever used kind "extraction" for a checklist run. */
function resolveView(
  task: Pick<AsyncTaskDetail, 'view' | 'kind'>
): AsyncTaskView {
  if (task.view) return task.view;
  return task.kind === 'extraction' ? 'checklist' : 'units';
}

/** Result treatment, falling back to the pre-`result_view` contract. */
function resolveResultView(
  task: Pick<AsyncTaskDetail, 'result_view' | 'kind'>
): AsyncTaskResultView {
  if (task.result_view) return task.result_view;
  return task.kind === 'extraction' ? 'json' : 'text';
}

function taskToSummary(task: AsyncTaskDetail, runId: string): AsyncTaskSummary {
  return {
    run_id: runId,
    name: task.name,
    status: task.status,
    total: task.total ?? 0,
    processed: task.processed ?? 0,
    failed_count: task.failed_count ?? 0,
    shard: task.shard,
    shard_count: task.shard_count,
    parent_run_id: task.parent_run_id,
    retried_from: null,
    kind: task.kind,
    view: task.view,
    result_view: task.result_view,
    steps: task.steps,
    stage_payload: task.stage_payload,
  };
}

function SiblingRow({
  sibling,
  conversationId,
  defaultExpanded = false,
}: Readonly<{
  sibling: AsyncTaskSummary;
  conversationId: string | null | undefined;
  defaultExpanded?: boolean;
}>) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  // Optimistic override so the row flips to Cancelled immediately instead of
  // waiting for the parent's next list poll.
  const [localStatus, setLocalStatus] = useState<Status | null>(null);
  const { cancel, cancelling } = useCancelRun(
    conversationId,
    sibling.run_id,
    () => setLocalStatus('cancelled')
  );
  const status = localStatus ?? (sibling.status as Status);
  const meta = STATUS_META[status] ?? STATUS_META.running;
  const pct =
    sibling.total > 0
      ? Math.round((sibling.processed / sibling.total) * 100)
      : 0;
  const isTerminal = TERMINAL.has(status);

  return (
    <div className={styles.siblingRow}>
      {/* Toggle and cancel are sibling buttons — nesting a button inside a
          button is invalid HTML and breaks click handling. */}
      <div className={styles.siblingRowHeader}>
        <button
          type="button"
          className={styles.siblingRowToggle}
          onClick={() => setExpanded((v) => !v)}
        >
          <FontAwesomeIcon
            icon={expanded ? faChevronDown : faChevronRight}
            style={{
              fontSize: 10,
              color: 'var(--mantine-color-dimmed)',
              flexShrink: 0,
            }}
          />
          <Text size="xs" fw={500} className={styles.siblingPart}>
            Part {sibling.shard}
          </Text>
          <TaskProgressBar
            pct={pct}
            color={meta.color}
            size="sm"
            className={styles.siblingProgress}
          />
          <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
            {sibling.processed}/{sibling.total}
          </Text>
          <Badge size="xs" color={meta.color} variant="light" radius="sm">
            {meta.label}
          </Badge>
        </button>
        {!isTerminal && (
          <CancelButton cancelling={cancelling} onCancel={cancel} />
        )}
      </div>
      <Collapse in={expanded}>
        <div className={styles.siblingDetail}>
          <AsyncTaskProgress
            runId={sibling.run_id}
            name={sibling.name}
            conversationId={conversationId ?? undefined}
            nested
          />
        </div>
      </Collapse>
    </div>
  );
}

export default function AsyncTaskProgress({
  runId,
  name,
  nested = false,
  conversationId: conversationIdProp,
  isLastMessage = false,
  onSendMessage,
}: AsyncTaskProgressProps) {
  const showSiblings = !nested;
  const conversationId = conversationIdProp ?? null;
  const [task, setTask] = useState<AsyncTaskDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  // Work queue is primary UX — instructions should be visible without a click.
  const [showUnits, setShowUnits] = useState(true);
  const [siblings, setSiblings] = useState<AsyncTaskSummary[]>([]);
  // The retry run spawned from this card, rendered as its own card below.
  // One retry per card — further retries chain from the retry card itself.
  const [retryRun, setRetryRun] = useState<RetryRun | null>(null);
  // Retry gating stays closed until rehydration settles: on a remount the
  // retried_from lookup is async, and until it answers we cannot know
  // whether a retry already exists — a click in that window would POST a
  // duplicate and orphan the first (retries have no chat message).
  const [retryStateKnown, setRetryStateKnown] = useState(false);
  // Mirrors the poll loop's delivery wait so the UI can show a combining/
  // delivery indicator instead of claiming Completed while the server is
  // still writing the combined result and sending deliveries. Falls back to
  // false when the bounded wait gives up, so the spinner cannot run forever.
  const [awaitingDelivery, setAwaitingDelivery] = useState(false);

  useEffect(() => {
    // The (conversationId, runId) identity changed — anything currently in
    // state belongs to the previous identity. Reset it so the card never
    // shows the prior run's progress, an Expired/error state that a stale
    // poll (e.g. mid-chat-switch 404) produced for a different task, or
    // the previous run's shard rows / expanded unit list.
    setTask(null);
    setNotFound(false);
    setError(null);
    setSiblings([]);
    setShowUnits(true);
    setRetryRun(null);
    setRetryStateKnown(false);
    setAwaitingDelivery(false);

    if (!conversationId || !runId) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    // The backend writes delivery_result AFTER the run flips to completed
    // (the whole-view LLM pass runs post-finalize, and sharded groups
    // deliver only when the last part finishes). Keep polling a completed
    // run until the result lands, bounded so cards for runs that never get
    // one (e.g. sibling parts — results land on the parent) stop cleanly.
    let deliveryWaitPolls = 0;
    const MAX_DELIVERY_WAIT_POLLS = 10; // ~5 minutes at 30s polls

    const poll = async () => {
      try {
        const res = await getAsyncTask(conversationId, runId);
        if (cancelled) return;
        const data = (res as any)?.data ?? res;
        setTask(data);
        setError(null);
        // The delivery wait is decided by DATA, not rendering context: only
        // the group's coordinating run receives the combined delivery_result,
        // so a child shard (parent_run_id pointing at another run) must not
        // poll for it — while a coordinator keeps polling even when rendered
        // nested inside another part's card, or its Result never appears.
        const isChildShard =
          Boolean(data.parent_run_id) && data.parent_run_id !== runId;
        // Poll until a deliverable lands: either the summary text or the
        // artifact manifest. Artifacts end the wait on their own — the card
        // hides the combined-result panel whenever a downloadable artifact
        // exists, so there is nothing left for the delivery_result write
        // (which lands moments after the manifest) to refresh.
        const hasDeliverable = Boolean(
          data.delivery_result || data.delivery_artifacts?.length
        );
        const stillAwaitingDelivery =
          data.status === 'completed' &&
          !hasDeliverable &&
          !isChildShard &&
          deliveryWaitPolls < MAX_DELIVERY_WAIT_POLLS;
        if (stillAwaitingDelivery) {
          deliveryWaitPolls += 1;
        }
        setAwaitingDelivery(stillAwaitingDelivery);
        // Checklist cards refresh at ~2s to track live ES step progress. The
        // post-completion delivery wait (whole-view LLM pass) is slow, so keep
        // that phase — and every decomposed run — at the 30s cadence.
        const nextDelay =
          resolveView(data) === 'checklist' && !stillAwaitingDelivery
            ? STEP_POLL_INTERVAL_MS
            : POLL_INTERVAL_MS;
        if (!TERMINAL.has(data.status) || stillAwaitingDelivery) {
          timer = setTimeout(poll, nextDelay);
        }
      } catch (e: any) {
        if (cancelled) return;
        const status = (e as any)?.response?.status ?? (e as any)?.status;
        if (status === 404) {
          // Run state expired from Redis (48h TTL) or was lost to a server
          // restart — render a terminal "expired" card, don't keep loading.
          setNotFound(true);
          setError(null);
          return;
        }
        setError(e?.message ?? 'Failed to fetch task');
        timer = setTimeout(poll, ERROR_RETRY_MS);
      }
    };

    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [conversationId, runId]);

  // The backend stamps parent_run_id on EVERY part of a sharded group —
  // self-referentially on the coordinating run — so it is the grouping key.
  // Defensive fallback: if a run says it's sharded (shard_count > 1) but the
  // stamp is missing, use the run's own id — children point at the
  // coordinator's run id, so that is the correct group key for its card.
  let groupKey = task?.parent_run_id;
  if (!groupKey && task && Number(task.shard_count) > 1) {
    groupKey = runId;
  }

  useEffect(() => {
    if (!showSiblings || !conversationId || !groupKey) {
      // No group to show for this identity (e.g. an unsharded run after a
      // sharded one) — don't leave the previous run's shard rows rendered.
      setSiblings([]);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    // A sharded group always has siblings, but they may not appear in
    // listAsyncTasks yet while parts are still spinning up. An empty list
    // must keep polling (group.every() is vacuously true on []), bounded
    // so a group whose siblings never materialize stops cleanly.
    let emptyPolls = 0;
    const MAX_EMPTY_POLLS = 10; // ~5 minutes at 30s polls

    const poll = async () => {
      try {
        const res = await listAsyncTasks(conversationId);
        if (cancelled) return;
        const data = (res as any)?.data ?? res;
        // A missing/null tasks array must not throw into the catch path —
        // that would retry forever without ever rendering peer parts.
        const tasks: AsyncTaskSummary[] = Array.isArray(data?.tasks)
          ? data.tasks
          : [];
        const group = tasks.filter(
          (t) => t.parent_run_id === groupKey && t.run_id !== runId
        );
        group.sort((a, b) => Number(a.shard) - Number(b.shard));
        setSiblings(group);
        const awaitingSiblings =
          group.length === 0 && emptyPolls < MAX_EMPTY_POLLS;
        if (awaitingSiblings) {
          emptyPolls += 1;
        }
        const allDone =
          group.length > 0 &&
          group.every((t) => TERMINAL.has(t.status as Status));
        if (!allDone && (group.length > 0 || awaitingSiblings)) {
          timer = setTimeout(poll, POLL_INTERVAL_MS);
        }
      } catch {
        if (cancelled) return;
        timer = setTimeout(poll, ERROR_RETRY_MS);
      }
    };

    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [showSiblings, conversationId, groupKey, runId]);

  // Retry rehydration: retryRun is component state, so a conversation switch
  // would otherwise orphan an in-flight retry (retries have no chat message
  // of their own). One list lookup when the source run first loads terminal
  // re-attaches the newest retry spawned from this run. Top-level cards
  // only, matching the Retry button: a nested sibling row must not mount a
  // retry card of its own (or issue a list request per sibling).
  const hasTerminalTask = Boolean(task && TERMINAL.has(task.status));
  useEffect(() => {
    if (nested || !hasTerminalTask || !conversationId || !runId) return;

    let cancelled = false;
    listAsyncTasks(conversationId)
      .then((res) => {
        if (cancelled) return;
        const data = (res as any)?.data ?? res;
        const tasks: AsyncTaskSummary[] = Array.isArray(data?.tasks)
          ? data.tasks
          : [];
        const existing = tasks.filter((t) => t.retried_from === runId);
        const newest = existing[existing.length - 1];
        if (newest) {
          setRetryRun(
            (prev) => prev ?? { run_id: newest.run_id, name: newest.name }
          );
        }
        // HARD BACKEND DEPENDENCY (chat-agent #527): rehydration only works
        // when the list endpoint exposes `retried_from` (key present on
        // every summary, value or null). Against an older backend the
        // filter above matches nothing and opening the gate would silently
        // re-arm Retry after a remount — the duplicate-orphan case all the
        // guards exist to prevent. Fail CLOSED instead: no field, no
        // button. An empty list is safe to open on (a retry would itself
        // be a task in this conversation's list).
        const fieldSupported =
          tasks.length === 0 || tasks.some((t) => 'retried_from' in t);
        if (fieldSupported) setRetryStateKnown(true);
      })
      .catch(() => {
        // Rehydration is best-effort — but an unanswered lookup must not
        // keep Retry locked forever, so a failure still opens the gate.
        if (!cancelled) setRetryStateKnown(true);
      });
    return () => {
      cancelled = true;
    };
  }, [nested, hasTerminalTask, conversationId, runId]);

  // Optimistically flip local state so the card updates immediately; the
  // next poll confirms against the server.
  const { cancel, cancelling } = useCancelRun(conversationId, runId, () =>
    setTask((prev) => (prev ? { ...prev, status: 'cancelled' } : prev))
  );

  const { retry, retrying, retryError } = useRetryFailed(
    conversationId,
    runId,
    setRetryRun
  );

  // Staged the moment the run reports answers, without a press. The extractor
  // keyed them by form field id, so there is nothing for a person or a model to
  // decide here — the decision is per answer, on the form.
  const staging = useStageExtractedAnswers(task?.stage_payload ?? null);

  if (error && !task) {
    return (
      <div className={styles.card}>
        <Group gap="xs">
          <ThemeIcon size="xs" radius="xl" color="red" variant="light">
            <FontAwesomeIcon
              icon={faCircleExclamation}
              style={{ fontSize: 10 }}
            />
          </ThemeIcon>
          <Text size="sm" c="red">
            {error}
          </Text>
        </Group>
      </div>
    );
  }

  if (notFound) {
    return (
      <PlaceholderCard
        nested={nested}
        title={name || 'Background Task'}
        badge="Expired"
        dimmedTitle
      >
        <Text size="xs" c="dimmed">
          This task's status is no longer available — results are kept for 48
          hours after a run starts.
        </Text>
      </PlaceholderCard>
    );
  }

  if (!task) {
    return (
      <PlaceholderCard
        nested={nested}
        title={name || 'Background Task'}
        badge="Loading"
      >
        <Group gap="xs">
          <Loader size="xs" />
          <Text size="xs" c="dimmed">
            Fetching task status…
          </Text>
        </Group>
      </PlaceholderCard>
    );
  }

  const { status } = task;
  const total = task.total ?? 0;
  const processed = task.processed ?? 0;
  const failedCount = task.failed_count ?? 0;
  const pct = total > 0 ? Math.round((processed / total) * 100) : 0;
  const isTerminal = TERMINAL.has(status);
  const isPreparing = status === 'preparing' || status === 'decomposing';
  // Two independent server-driven treatments, so a new task kind needs no
  // change here: a step run shows named steps instead of the units counter (a
  // plain "Extracting…" line until the server publishes them), and a JSON
  // result surfaces via the extracted-data viewer.
  const isStepRun = resolveView(task) === 'checklist';
  const hasJsonResult = resolveResultView(task) === 'json';
  const checklistSteps = task.steps ?? [];
  // Units are all done but the combined result hasn't been written (and
  // deliveries haven't been sent) — the server is still finalizing. Guarded
  // on the live task fields, not just the poll flag, so an optimistic local
  // status flip (e.g. cancel) can never render a stale finalizing state. Step
  // runs aren't decomposed into units, so they never reach that phase.
  const finalizing =
    !isStepRun &&
    awaitingDelivery &&
    status === 'completed' &&
    !task.delivery_result &&
    !task.delivery_artifacts?.length;
  const meta = STATUS_META[status];
  const accentClass = styles[ACCENT_CLASS[finalizing ? 'running' : status]];

  const shardCount = task.shard_count ? Number(task.shard_count) : null;
  const shardIdx = task.shard ? Number(task.shard) : null;
  // Top-level multi-part runs: show a group shell with Total + equal peer
  // part rows (OMNI-597), including preparing/planning so peers are not
  // hidden until the coordinator leaves those statuses. Nested cards keep
  // the single-part progress UI.
  const isShardedGroup = showSiblings && shardCount != null && shardCount > 1;

  const groupParts: AsyncTaskSummary[] = isShardedGroup
    ? [taskToSummary(task, runId), ...siblings].sort(
        (left, right) => Number(left.shard) - Number(right.shard)
      )
    : [];
  // Only show Total once every part is present AND units have been planned.
  // Summing an incomplete peer list understates the run; summing zeros while
  // parts are still preparing/decomposing misstates work not yet planned.
  const groupPartsComplete =
    shardCount != null && groupParts.length === shardCount;
  const groupTotalUnits = groupPartsComplete
    ? groupParts.reduce((sum, part) => sum + (part.total ?? 0), 0)
    : null;
  const showGroupTotal = groupTotalUnits != null && groupTotalUnits > 0;

  // Retry gating must consider the WHOLE shard group, not just this run:
  // retry-failed collects failed units across every part, so the button must
  // wait until all parts are terminal (retrying mid-group would miss units
  // still accumulating failures) and must appear even when only a sibling
  // part failed. For a sharded run whose sibling list hasn't loaded yet the
  // group state is unknown — hold the button until it arrives (unsharded
  // runs have no siblings and pass both checks vacuously).
  const siblingsKnown = !(shardCount && shardCount > 1) || siblings.length > 0;
  const groupTerminal =
    isTerminal &&
    siblingsKnown &&
    siblings.every((s) => TERMINAL.has(s.status as Status));
  // Failures on CANCELLED parts don't count — the user chose to stop that
  // work, so no retry nudge. But a cancelled top-level card must not hide
  // failures on sibling parts that ran to completion: retry-failed is
  // group-scoped and this card is the group's only Retry entry point.
  const retryableFailures =
    (status !== 'cancelled' && failedCount > 0) ||
    siblings.some((s) => s.status !== 'cancelled' && (s.failed_count ?? 0) > 0);
  const canRetry =
    groupTerminal &&
    retryableFailures &&
    !retryRun &&
    retryStateKnown &&
    !nested;

  // Continue appears once a JSON-result run — one that feeds the next workflow
  // step — finishes; nested part cards never own it, and a prose-result run has
  // nothing to continue into.
  const showContinue =
    hasJsonResult &&
    !nested &&
    status === 'completed' &&
    Boolean(onSendMessage);
  const showDataViewer =
    hasJsonResult && isTerminal && Boolean(task.delivery_result);
  // Gated on `stage_payload` alone, never on `kind`/`view`/`result_view`: the
  // server decides whether a run has answers to put on a form.
  const showStaging =
    !nested && status === 'completed' && Boolean(task.stage_payload);
  const showResultActions = showDataViewer || showContinue;

  const groupDisplayName = name || task.name || 'Background Task';
  const showRetryChild = Boolean(retryRun) && (isShardedGroup || !nested);
  const shardBadge = buildShardBadge(isShardedGroup, shardCount, shardIdx);

  const errorAndDelivery = (
    <ErrorAndDelivery
      error={task.error}
      // Dropped once the hand-off card is on screen: it already says how many
      // answers landed and that nothing was saved, so the panel repeated the
      // same sentence in a resizable box the user has to scroll past.
      deliveryResult={showStaging ? null : task.delivery_result}
      deliveryArtifacts={task.delivery_artifacts}
      isTerminal={isTerminal}
      finalizing={finalizing}
      taskName={groupDisplayName}
      hasJsonResult={hasJsonResult}
    />
  );
  const retryFooter = (
    <RetryFooter
      retryError={retryError}
      showRetryChild={showRetryChild}
      retryRun={retryRun}
      conversationId={conversationId}
    />
  );

  const capNotice = <CapNotice cappedFrom={task.capped_from} total={total} />;

  let body: ReactNode;
  if (!isShardedGroup && isPreparing) {
    body = (
      <Stack gap={6}>
        <PreparingLine status={status} />
        {capNotice}
      </Stack>
    );
  } else {
    body = (
      <Stack gap={6}>
        {isShardedGroup && isPreparing ? (
          <PreparingLine status={status} />
        ) : null}
        {!isShardedGroup && isStepRun ? (
          <>
            {checklistSteps.length > 0 ? (
              <StepsChecklist
                steps={checklistSteps}
                defaultOpen={!isTerminal}
              />
            ) : (
              <>
                <TaskProgressBar
                  pct={pct}
                  color={isTerminal && !finalizing ? meta.color : 'blue'}
                  size="lg"
                  animated={!isTerminal || finalizing}
                />
                {/* Spinner only while running — a terminal run with an
                    unpublished step list (e.g. cancelled early) must not read
                    as still starting under its terminal badge. */}
                {!isTerminal && <StartingLine />}
              </>
            )}
            {capNotice}
          </>
        ) : null}
        {!isShardedGroup && !isStepRun ? (
          <>
            <TaskProgressBar
              pct={pct}
              color={isTerminal && !finalizing ? meta.color : 'blue'}
              size="lg"
              animated={!isTerminal || finalizing}
              showLabel
            />
            <Group justify="space-between" wrap="nowrap">
              <Text size="xs" c="dimmed">
                {processed} / {total} units
              </Text>
              <Group gap={8} wrap="nowrap">
                {failedCount > 0 && (
                  <Text size="xs" c="red" fw={500}>
                    {failedCount} failed
                  </Text>
                )}
                {pct < 12 && (
                  <Text size="xs" c="dimmed">
                    {pct}%
                  </Text>
                )}
              </Group>
            </Group>
            {capNotice}
          </>
        ) : null}
        {/* Outside both progress branches: `view` and `result_view` are
            independent, so a units-view run can still carry a JSON result or a
            pending action. Mirrors the draft cards (EmailDraftCard header
            actions) — right-aligned, neutral secondary first, accent primary
            last. Sharded groups delegate result actions to their parts. */}
        {!isShardedGroup && showResultActions ? (
          <div className={styles.actionsRow}>
            {showDataViewer && task.delivery_result ? (
              <ExtractionDataButton result={task.delivery_result} />
            ) : null}
            {showContinue ? (
              <ContinueButton
                isLastMessage={isLastMessage}
                onContinue={() => onSendMessage?.('Continue')}
              />
            ) : null}
          </div>
        ) : null}
        {/* The receipt for the staging this card fired itself. Read-only:
            there is no Apply here, because the answers are already on the form
            and the accept/reject happens there, next to each question. */}
        {showStaging && task.stage_payload ? (
          <FormDraftHandoff
            title="Answers from the document"
            formName={task.stage_payload.formName}
            answerCount={task.stage_payload.answeredCount}
            result={staging.outcome ?? undefined}
            pending={staging.pending}
            onRetry={staging.retry}
          />
        ) : null}
        {errorAndDelivery}
        {isShardedGroup ? (
          <>
            <SectionLabel>Parts</SectionLabel>
            <Stack gap={0} className={styles.partsList}>
              {groupParts.map((part) => (
                <SiblingRow
                  key={part.run_id}
                  sibling={part}
                  conversationId={conversationId}
                  defaultExpanded={
                    part.run_id === runId ||
                    !TERMINAL.has(part.status as Status)
                  }
                />
              ))}
            </Stack>
          </>
        ) : (
          task.units &&
          task.units.length > 0 && (
            <>
              <Divider my={2} />
              <button
                type="button"
                className={styles.unitsToggle}
                onClick={() => setShowUnits((v) => !v)}
              >
                <FontAwesomeIcon
                  icon={showUnits ? faChevronDown : faChevronRight}
                  style={{ fontSize: 10 }}
                />
                <span>
                  {showUnits ? 'Hide' : 'Show'} {task.units.length} units
                </span>
              </button>
              <Collapse in={showUnits}>
                <Stack gap={0}>
                  {/* Key includes runId so a run change remounts rows —
                      local expanded/detail state must not survive onto a
                      different run's unit at the same index. */}
                  {task.units.map((u) => (
                    <AsyncTaskUnitRow
                      key={`${runId}-${u.index}`}
                      unit={u}
                      runId={runId}
                      conversationId={conversationId}
                    />
                  ))}
                </Stack>
              </Collapse>
            </>
          )
        )}
        {retryFooter}
      </Stack>
    );
  }

  return (
    <div
      className={`${styles.card} ${accentClass ?? ''} ${
        nested && !isShardedGroup ? styles.cardNested : ''
      }`}
    >
      <Group justify="space-between" mb={6} wrap="nowrap" align="flex-start">
        <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
          <Text size="sm" fw={600} lineClamp={1}>
            {groupDisplayName}
          </Text>
          {isShardedGroup && showGroupTotal && (
            <Tooltip label="Total units across all parts of this run">
              <Badge size="sm" color="gray" variant="light" radius="sm">
                Total: {groupTotalUnits} units
              </Badge>
            </Tooltip>
          )}
          {shardBadge}
        </Group>
        <Group gap={6} wrap="nowrap">
          <StatusBadge status={status} finalizing={finalizing} />
          {/* Retry re-runs only FAILED units (whole shard group) as a new
              run. Cancelled runs are excluded — their unfinished units were
              abandoned deliberately, not failed. Top-level card only, like
              Cancel: a nested part's retry would retry the entire group. */}
          {canRetry && <RetryButton retrying={retrying} onRetry={retry} />}
          {!isShardedGroup && !isTerminal && !nested && (
            <CancelButton cancelling={cancelling} onCancel={cancel} />
          )}
        </Group>
      </Group>
      {body}
    </div>
  );
}
